import { DevicesOrderBy, HardwareOptionInput } from '@/__generated__/graphql';
import FETCH_DEVICE_NAMING from '@/graphql/querys/fetchDeviceNaming';
import { type Device } from '@/graphql/querys/fetchDeviceProfile';
import { setSaveHardwareConfigurationModal } from '@/slices/modals';
import { useDispatch } from '@/store';
import { useQuery } from '@apollo/client';
import ToolbarSkeleton from '@components/ToolbarSkeleton';
import DeleteDeviceConfiguration from './DeleteDeviceConfiguration';
import DeviceConfigurationTable from './DeviceConfigurationTable';
import SummarySheetConfiguration from './SummarySheetConfiguration';
import StandardListViewHeader from '@features/standardDesign/components/listView/StandardListViewHeader';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { FilterList } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdvancedFiltersModal, { AdvancedFiltersValue } from './AdvancedFiltersModal';
import { decodeValue, encodeValue } from '@/utils/base64';

const DEFAULT_PAGE = 1;
const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ORDER_BY = DevicesOrderBy.NameAsc;

const DeviceConfiguration = () => {
  const { _ } = useLingui();
  const [isOpenAdvancedFiltersModal, setIsOpenAdvancedFiltersModal] = useState(false);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: deviceTypesData, loading: deviceTypesLoading } = useQuery(FETCH_DEVICE_NAMING);
  const deviceTypeOptions =
    deviceTypesData?.deviceNaming.map(({ type }) => ({ value: type, label: type })) ?? [];

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSummarySheetConfiguration, setShowSummarySheetConfiguration] = useState(false);

  const type = searchParams.get('type');
  const searchDevice = searchParams.get('device');
  const searchProject = searchParams.get('project');
  const organizationId = searchParams.get('organization')
    ? Number(searchParams.get('organization'))
    : null;
  const page = Number(searchParams.get('page') || DEFAULT_PAGE);
  const rowsPerPage = Number(searchParams.get('rows') || DEFAULT_ROWS_PER_PAGE);
  const orderBy = Object.values(DevicesOrderBy).includes(
    searchParams.get('order') as DevicesOrderBy,
  )
    ? (searchParams.get('order') as DevicesOrderBy)
    : DEFAULT_ORDER_BY;

  const setFilters = useCallback(
    (filters: Record<string, string | null>) => {
      setSearchParams((searchParams) => {
        const params = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(filters)) {
          if (value) params.set(key, value);
          else params.delete(key);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  const advancedFiltersValue = useMemo((): AdvancedFiltersValue => {
    const optionsParams = searchParams.get('hardwareOptions');
    const serialNumbersParams = searchParams.get('hardwareSerialNumbers');
    const imageVersionsParams = searchParams.get('hardwareImageVersions');
    const iccidsParams = searchParams.get('hardwareIccids');
    const imeisParams = searchParams.get('hardwareImeis');
    const sensorProtocolsParams = searchParams.get('hardwareSensorProtocols');

    let hardwareOptions: AdvancedFiltersValue['hardwareOptions'] = [];
    if (optionsParams) {
      try {
        const parsedHardwareOptions =
          decodeValue<AdvancedFiltersValue['hardwareOptions']>(optionsParams) ?? [];
        if (
          Array.isArray(parsedHardwareOptions) &&
          parsedHardwareOptions.every(
            (entry) =>
              'id' in entry &&
              typeof entry.id === 'number' &&
              'type' in entry &&
              typeof entry.type === 'string',
          )
        ) {
          hardwareOptions = parsedHardwareOptions;
        } else {
          throw new Error(`Unexpected search param "hardwareOptions": ${parsedHardwareOptions}`);
        }
      } catch (e) {
        console.error(e);
      }
    }

    let hardwareSerialNumbers: string[] = [];
    if (serialNumbersParams) {
      try {
        const parsedSerialNumbers = JSON.parse(serialNumbersParams);
        if (
          Array.isArray(parsedSerialNumbers) &&
          parsedSerialNumbers.every((entry) => typeof entry === 'string')
        ) {
          hardwareSerialNumbers = parsedSerialNumbers;
        } else {
          throw new Error(
            `Unexpected search param "hardwareSerialNumbers": ${parsedSerialNumbers}`,
          );
        }
      } catch (e) {
        console.error(e);
      }
    }

    let hardwareImageVersions: string[] = [];
    if (imageVersionsParams) {
      try {
        const parsedImageVersions = JSON.parse(imageVersionsParams);
        if (
          Array.isArray(parsedImageVersions) &&
          parsedImageVersions.every((entry) => typeof entry === 'string')
        ) {
          hardwareImageVersions = parsedImageVersions;
        } else {
          throw new Error(
            `Unexpected search param "hardwareImageVersions": ${parsedImageVersions}`,
          );
        }
      } catch (e) {
        console.error(e);
      }
    }

    let hardwareIccids: string[] = [];
    if (iccidsParams) {
      try {
        const parsed = JSON.parse(iccidsParams);
        if (Array.isArray(parsed) && parsed.every((entry) => typeof entry === 'string')) {
          hardwareIccids = parsed;
        } else {
          throw new Error(`Unexpected search param "hardwareIccids": ${parsed}`);
        }
      } catch (e) {
        console.error(e);
      }
    }

    let hardwareImeis: string[] = [];
    if (imeisParams) {
      try {
        const parsed = JSON.parse(imeisParams);
        if (Array.isArray(parsed) && parsed.every((entry) => typeof entry === 'string')) {
          hardwareImeis = parsed;
        } else {
          throw new Error(`Unexpected search param "hardwareImeis": ${parsed}`);
        }
      } catch (e) {
        console.error(e);
      }
    }

    let hardwareSensorProtocols: string[] = [];
    if (sensorProtocolsParams) {
      try {
        const parsed = JSON.parse(sensorProtocolsParams);
        if (Array.isArray(parsed) && parsed.every((entry) => typeof entry === 'string')) {
          hardwareSensorProtocols = parsed;
        } else {
          throw new Error(`Unexpected search param "hardwareSensorProtocols": ${parsed}`);
        }
      } catch (e) {
        console.error(e);
      }
    }

    return {
      hardwareOptions,
      hardwareSerialNumbers,
      hardwareImageVersions,
      hardwareIccids,
      hardwareImeis,
      hardwareSensorProtocols,
    };
  }, [
    searchParams.get('hardwareOptions'),
    searchParams.get('hardwareSerialNumbers'),
    searchParams.get('hardwareImageVersions'),
    searchParams.get('hardwareIccids'),
    searchParams.get('hardwareImeis'),
    searchParams.get('hardwareSensorProtocols'),
  ]);

  const handleTypeChange = (value: string | null | undefined) => {
    const type = typeof value === 'string' ? value : null;
    setFilters({ type });
  };

  const handleSearchDeviceInput = (value: string | null | undefined) => {
    const device = typeof value === 'string' ? value : null;
    setFilters({ device });
  };

  const handleSearchProjectInput = (value: string | null | undefined) => {
    const project = typeof value === 'string' ? value : null;
    setFilters({ project });
  };

  const handleOrganizationChange = (value: number | null) => {
    const organization = typeof value === 'number' ? value.toString() : null;
    setFilters({ organization });
  };

  const handlePageChange = (value: number) => {
    const page = value === DEFAULT_PAGE ? null : value.toString();
    setFilters({ page });
  };

  const handleRowsPerPageChange = (value: number) => {
    const rows = value === DEFAULT_ROWS_PER_PAGE ? null : value.toString();
    setFilters({ rows });
  };

  const handleOrderByChange = (value: DevicesOrderBy) => {
    const order = value === DEFAULT_ORDER_BY ? null : value;
    setFilters({ order });
  };

  const onCloseAdvancedFiltersModal = () => {
    setIsOpenAdvancedFiltersModal(false);
  };

  const handleOpenAdvancedFiltersModal = () => {
    setIsOpenAdvancedFiltersModal(true);
  };

  const handleAdvancedFiltersChange = (value: AdvancedFiltersValue) => {
    const hardwareOptions =
      value.hardwareOptions.length > 0 ? encodeValue(value.hardwareOptions) : null;
    const hardwareSerialNumbers =
      value.hardwareSerialNumbers.length > 0 ? JSON.stringify(value.hardwareSerialNumbers) : null;
    const hardwareImageVersions =
      value.hardwareImageVersions.length > 0 ? JSON.stringify(value.hardwareImageVersions) : null;
    const hardwareIccids =
      value.hardwareIccids.length > 0 ? JSON.stringify(value.hardwareIccids) : null;
    const hardwareImeis =
      value.hardwareImeis.length > 0 ? JSON.stringify(value.hardwareImeis) : null;
    const hardwareSensorProtocols =
      value.hardwareSensorProtocols.length > 0
        ? JSON.stringify(value.hardwareSensorProtocols)
        : null;
    setFilters({
      hardwareOptions,
      hardwareSerialNumbers,
      hardwareImageVersions,
      hardwareIccids,
      hardwareImeis,
      hardwareSensorProtocols,
    });
  };

  return (
    <>
      <AdvancedFiltersModal
        onSubmit={handleAdvancedFiltersChange}
        onClose={onCloseAdvancedFiltersModal}
        open={isOpenAdvancedFiltersModal}
        value={advancedFiltersValue}
      />
      <Stack
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <Suspense fallback={<ToolbarSkeleton />}>
          <StandardListViewHeader
            searchFilter={{
              value: searchDevice,
              onChange: handleSearchDeviceInput,
              label: _(msg`Buscar dispositivo`),
            }}
            organizationFilter={{
              value: organizationId,
              onChange: (_, value) => handleOrganizationChange(value),
            }}
            customSelectorFilters={[
              {
                type: 'text',
                value: searchProject,
                onChange: handleSearchProjectInput,
                label: _(msg`Buscar proyecto`),
              },
              {
                type: 'autocomplete',
                value: type,
                options: deviceTypeOptions,
                onChange: handleTypeChange,
                label: _(msg`Tipo de dispositivo`),
                loading: deviceTypesLoading,
              },
            ]}
            actionButtons={[
              {
                label: _(msg`Filtros avanzados`),
                onClick: handleOpenAdvancedFiltersModal,
                ButtonProps: {
                  variant:
                    advancedFiltersValue.hardwareOptions.length > 0 ||
                      advancedFiltersValue.hardwareSerialNumbers.length > 0 ||
                      advancedFiltersValue.hardwareImageVersions.length > 0 ||
                      advancedFiltersValue.hardwareIccids.length > 0 ||
                      advancedFiltersValue.hardwareImeis.length > 0 ||
                      advancedFiltersValue.hardwareSensorProtocols.length > 0
                      ? 'contained'
                      : 'outlined',
                  color: 'info',
                  endIcon: <FilterList />,
                },
              },
            ]}
          />
        </Suspense>
        <DeviceConfigurationTable
          type={type}
          searchDeviceInput={searchDevice}
          searchProjectInput={searchProject}
          organizationId={organizationId}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          hardwareOptions={advancedFiltersValue.hardwareOptions}
          hardwareSerialNumbers={advancedFiltersValue.hardwareSerialNumbers}
          hardwareImageVersions={advancedFiltersValue.hardwareImageVersions}
          hardwareIccids={advancedFiltersValue.hardwareIccids}
          hardwareImeis={advancedFiltersValue.hardwareImeis}
          hardwareSensorProtocols={advancedFiltersValue.hardwareSensorProtocols}
          handlePageChange={handlePageChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          handleOrderByChange={handleOrderByChange}
          handleCreateConfig={(device: Device) => {
            dispatch(
              setSaveHardwareConfigurationModal({
                open: true,
                deviceId: device.id,
              }),
            );
            setSelectedDevice(device);
          }}
          handleDeleteConfig={(device: Device) => {
            setShowDeleteDialog(true);
            setSelectedDevice(device);
          }}
          handleViewConfigDrawer={(device: Device) => {
            setShowSummarySheetConfiguration(true);
            setSelectedDevice(device);
          }}
        />
      </Stack>
      <DeleteDeviceConfiguration
        show={showDeleteDialog}
        device={selectedDevice}
        handleClose={() => {
          setShowDeleteDialog(false);
          setSelectedDevice(null);
        }}
      />
      <SummarySheetConfiguration
        open={showSummarySheetConfiguration}
        onClose={() => {
          setShowSummarySheetConfiguration(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
      />
    </>
  );
};

export default DeviceConfiguration;
